import React, { useState, useEffect } from 'react';
import { PackageCheck, Mail, Eye, RefreshCw, AlertCircle, Filter, CheckCircle2 } from 'lucide-react';
import { Order } from '../../types';

export const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const fetchOrders = async () => {
    try {
      const url = statusFilter === 'all' ? '/api/admin/orders' : `/api/admin/orders?status=${statusFilter}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json() as any;
        setOrders(data.orders || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const handleUpdateStatus = async (orderId: string, orderStatus: string) => {
    try {
      const res = await fetch('/api/admin/orders/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, orderStatus })
      });
      if (res.ok) {
        setActionMessage(`Statut mis à jour : ${orderStatus}`);
        fetchOrders();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(prev => prev ? { ...prev, order_status: orderStatus as any } : null);
        }
        setTimeout(() => setActionMessage(null), 3000);
      }
    } catch {
      alert('Erreur lors du changement de statut.');
    }
  };

  const handleResendEmail = async (orderId: string) => {
    try {
      const res = await fetch('/api/admin/orders/resend-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId })
      });
      if (res.ok) {
        setActionMessage('E-mail de confirmation réexpédié avec succès.');
        fetchOrders();
        setTimeout(() => setActionMessage(null), 3000);
      }
    } catch {
      alert('Erreur lors du renvoi de l’e-mail.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Gestion des Commandes</h2>
          <p className="text-xs text-gray-500">Suivi des paiements Stripe, expéditions et coordonnées clients</p>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs font-semibold p-2 bg-white border border-gray-200 rounded-xl"
          >
            <option value="all">Tous les statuts</option>
            <option value="paid">Payées</option>
            <option value="processing">En préparation</option>
            <option value="shipped">Expédiées</option>
            <option value="delivered">Livrées</option>
            <option value="cancelled">Annulées</option>
          </select>
        </div>
      </div>

      {actionMessage && (
        <div className="p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-xl text-xs font-semibold">
          {actionMessage}
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-gray-50 border-b text-gray-500 font-semibold uppercase tracking-wider">
            <tr>
              <th className="p-4">Référence</th>
              <th className="p-4">Date</th>
              <th className="p-4">Total</th>
              <th className="p-4">Paiement</th>
              <th className="p-4">Statut Commande</th>
              <th className="p-4">E-mail</th>
              <th className="p-4 text-right">Détails</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {orders.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-gray-400">
                  Aucune commande enregistrée.
                </td>
              </tr>
            ) : (
              orders.map(o => (
                <tr key={o.id} className="hover:bg-gray-50/50">
                  <td className="p-4 font-mono font-bold text-gray-900">{o.order_ref}</td>
                  <td className="p-4 text-gray-500">
                    {new Date(o.created_at).toLocaleDateString()} {new Date(o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="p-4 font-mono font-semibold">
                    {Number(o.total_amount).toFixed(2)} {o.currency}
                  </td>
                  <td className="p-4">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-bold ${
                        o.payment_status === 'paid'
                          ? 'bg-emerald-100 text-emerald-800'
                          : o.payment_status === 'failed'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {o.payment_status}
                    </span>
                  </td>
                  <td className="p-4 font-medium text-gray-700 capitalize">
                    {o.order_status}
                  </td>
                  <td className="p-4">
                    {o.confirmation_email_sent === 1 ? (
                      <span className="text-emerald-600 font-semibold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Envoyé
                      </span>
                    ) : (
                      <span className="text-gray-400">En attente</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => setSelectedOrder(o)}
                      className="p-1.5 text-gray-600 hover:text-pink-600 hover:bg-gray-100 rounded-lg"
                      title="Voir la commande"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-neutral-950/60 backdrop-blur-xs">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 shadow-2xl border border-gray-100 space-y-5 text-xs">
            <div className="flex justify-between items-center border-b pb-3">
              <div>
                <h3 className="text-base font-display font-bold text-gray-900">
                  Commande {selectedOrder.order_ref}
                </h3>
                <p className="text-gray-400 text-[11px]">ID interne: {selectedOrder.id}</p>
              </div>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-700 font-bold text-base">
                ✕
              </button>
            </div>

            {/* Customer & Shipping Section (ADMIN ONLY - NEVER PUBLIC) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Coordonnées Client</h4>
                <p className="text-gray-700 font-medium">{selectedOrder.customer_name || 'Nom non renseigné'}</p>
                <p className="text-gray-600">{selectedOrder.customer_email || 'E-mail non renseigné'}</p>
              </div>
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Adresse de Livraison</h4>
                {selectedOrder.shipping_address_json ? (
                  <pre className="font-sans text-gray-600 whitespace-pre-wrap">
                    {(() => {
                      try {
                        const addr = JSON.parse(selectedOrder.shipping_address_json);
                        return `${addr.line1 || ''}\n${addr.postal_code || ''} ${addr.city || ''}\n${addr.country || ''}`;
                      } catch {
                        return selectedOrder.shipping_address_json;
                      }
                    })()}
                  </pre>
                ) : (
                  <p className="text-gray-400">Non renseignée</p>
                )}
              </div>
            </div>

            {/* Items */}
            <div>
              <h4 className="font-bold text-gray-800 mb-2">Articles commandés</h4>
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                {selectedOrder.items?.map((it, idx) => (
                  <div key={idx} className="p-3 flex justify-between items-center bg-white">
                    <div>
                      <p className="font-bold text-gray-900">{it.product_name}</p>
                      {it.variant_name && <p className="text-gray-500 text-[11px]">{it.variant_name}</p>}
                      <p className="text-gray-400 font-mono text-[10px]">SKU: {it.sku}</p>
                    </div>
                    <div className="text-right font-mono">
                      <p>{it.quantity} × {Number(it.unit_price).toFixed(2)} {selectedOrder.currency}</p>
                      <p className="font-bold text-gray-900">{Number(it.total_price).toFixed(2)} {selectedOrder.currency}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions: Status transitions & Resend Email */}
            <div className="pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700">Changer statut :</span>
                <select
                  value={selectedOrder.order_status}
                  onChange={e => handleUpdateStatus(selectedOrder.id, e.target.value)}
                  className="p-1.5 border border-gray-200 rounded-lg bg-white font-medium"
                >
                  <option value="pending">En attente</option>
                  <option value="processing">En préparation</option>
                  <option value="shipped">Expédiée</option>
                  <option value="delivered">Livrée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleResendEmail(selectedOrder.id)}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <Mail className="w-3.5 h-3.5" />
                  <span>Renvoyer e-mail confirmation</span>
                </button>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="px-4 py-2 bg-neutral-900 text-white rounded-xl font-bold hover:bg-pink-600 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
